import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReleaseGraph, MonthTemperature, GenericFormula } from "../../../Models";
import { MulticoteBlend } from "../../../Models/ServerModels/AgriSolution";
import { NUTRIENT_RADIO_OPTIONS } from "../../../Constants";

interface SolutionSliceState {
    temperatureState: {
        temperatures: MonthTemperature[];
        selectedTemperatureUnit: string | undefined;
    };
    multicoteBlendsState: {
        multicoteBlends: MulticoteBlend[];
    };
    releaseGraphState: {
        isUserShare: boolean;
        shareLink: string;
        releaseGraphValues: ReleaseGraph[];
        currentDayReleaseRemainValuesForMulticoteSolution: GenericFormula[];
    };
    selectedNutrient: string | undefined;
    plantingDate: string | undefined;
    calculateSolutionResultInProgress: boolean;
    calculateSolutionResultIsSuccess: boolean;
    selectedBlendObject: MulticoteBlend | undefined;
    isUserSave: boolean;
    itemId: number | null;
    itemName: string;
    saveInProgress: boolean;
    userEditAfterGettingResult: boolean;
}

const initialState: SolutionSliceState = {
    selectedBlendObject: {
        id: 0,
        name: "",
        longevity: 0,
    },
    temperatureState: {
        temperatures: [],
        selectedTemperatureUnit: undefined,
    },
    selectedNutrient: NUTRIENT_RADIO_OPTIONS[0],
    multicoteBlendsState: {
        multicoteBlends: [],
    },
    calculateSolutionResultInProgress: false,
    calculateSolutionResultIsSuccess: false,
    releaseGraphState: {
        isUserShare: false,
        shareLink: "",
        releaseGraphValues: [],
        currentDayReleaseRemainValuesForMulticoteSolution: [],
    },
    plantingDate: undefined,
    isUserSave: false,
    itemId: null,
    itemName: "",
    saveInProgress: false,
    userEditAfterGettingResult: false,
};

export const solutionSlice = createSlice({
    name: "solution",
    initialState,
    reducers: {
        setTemperatureState: (
            state: SolutionSliceState,
            action: PayloadAction<{
                temperatures: MonthTemperature[];
                selectedTemperatureUnit: string | undefined;
            }>
        ) => {
            state.temperatureState = action.payload;
        },
        setUserEditAfterGettingResult: (state: SolutionSliceState, action: PayloadAction<boolean>) => {
            state.userEditAfterGettingResult = action.payload;
        },
        setCurrentDayReleaseRemainValuesForMulticoteSolution: (state: SolutionSliceState, action: PayloadAction<GenericFormula[]>) => {
            state.releaseGraphState.currentDayReleaseRemainValuesForMulticoteSolution = action.payload;
        },
        setSelectedBlendObject: (state: SolutionSliceState, action: PayloadAction<MulticoteBlend | undefined>) => {
            state.selectedBlendObject = action.payload;
        },
        setSelectedNutrient: (state: SolutionSliceState, action: PayloadAction<string | undefined>) => {
            state.selectedNutrient = action.payload;
        },

        setSelectedPlantingDate: (state: SolutionSliceState, action: PayloadAction<string | undefined>) => {
            state.plantingDate = action.payload;
        },
        setItemId: (state: SolutionSliceState, action: PayloadAction<number | null>) => {
            state.itemId = action.payload;
        },
        setItemName: (state: SolutionSliceState, action: PayloadAction<string>) => {
            state.itemName = action.payload;
        },
        setSaveInProgress: (state: SolutionSliceState, action: PayloadAction<boolean>) => {
            state.saveInProgress = action.payload;
        },

        setMulticoteBlends: (state: SolutionSliceState, action: PayloadAction<MulticoteBlend[]>) => {
            state.multicoteBlendsState.multicoteBlends = action.payload;
        },

        setCalculateSolutionResultInProgress: (state: SolutionSliceState, action: PayloadAction<boolean>) => {
            state.calculateSolutionResultInProgress = action.payload;
        },

        setCalculateSolutionResultIsSuccess: (state: SolutionSliceState, action: PayloadAction<boolean>) => {
            state.calculateSolutionResultIsSuccess = action.payload;
        },

        setReleaseGraphValues: (state: SolutionSliceState, action: PayloadAction<ReleaseGraph[]>) => {
            state.releaseGraphState.releaseGraphValues = action.payload;
        },
        setShareLink: (state: SolutionSliceState, action: PayloadAction<string>) => {
            state.releaseGraphState.shareLink = action.payload;
        },
        setIsUserShare: (state: SolutionSliceState, action: PayloadAction<boolean>) => {
            state.releaseGraphState.isUserShare = action.payload;
        },
        setIsUserSave: (state: SolutionSliceState, action: PayloadAction<boolean>) => {
            state.isUserSave = action.payload;
        },
        reset: () => initialState,
    },
});

export const Actions = solutionSlice.actions;
export const Reducer = solutionSlice.reducer;
